export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#F7F7F8] px-4 py-12" data-testid="public-privacy-page">
      <div className="max-w-[720px] mx-auto bg-white rounded-md border border-[#E4E4E7] p-8 sm:p-10">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#09090B]">Privacy Policy</h1>
        <p className="text-sm text-[#71717A] mt-2">Last updated: August 26, 2026</p>

        <div className="prose prose-sm max-w-none mt-6 text-[#27272A] space-y-5">
          <p>
            SprayOps ("the Service") is operated by Olimpo Web Design LLC ("we", "us"). This policy explains
            what data the Service handles, why, and how you can remove it.
          </p>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">1. Who this policy is for</h2>
            <p>
              The Service is a private content and social publishing tool used by a single insulation
              contracting business and its staff. It is not a consumer product and is not offered to the
              general public.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">2. Google user data</h2>
            <p>When you choose to connect your Google account, we request read-only access to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Google Search Console (webmasters.readonly)</strong> — the search queries, clicks,
                impressions and average position for websites you already own and have verified in Search
                Console.
              </li>
              <li>
                <strong>Google Analytics (analytics.readonly)</strong> — aggregate traffic and conversion
                metrics for analytics properties you already have access to.
              </li>
            </ul>
            <p>
              We use this data for one purpose: to show you, inside your own dashboard, which search terms
              bring visitors to your website, so you can decide what content to create next. We do not modify
              anything in your Google account. We never request write access.
            </p>
            <p>
              <strong>Limited Use.</strong> Our use of information received from Google APIs adheres to the
              Google API Services User Data Policy, including the Limited Use requirements. We do not sell
              Google user data, do not use it for advertising, do not transfer it to third parties except as
              needed to provide this Service, and do not allow humans to read it except with your explicit
              consent, for security purposes, to comply with applicable law, or where the data is aggregated
              and anonymized.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">3. Meta (Facebook and Instagram) data</h2>
            <p>
              When you connect a Facebook Page or Instagram account, we store the access credential needed to
              publish the posts you schedule, plus the page name and identifier. We publish only what you
              create and schedule in the Service. We do not read your personal profile, your friends, or your
              private messages.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">4. Contact information you collect</h2>
            <p>
              When someone submits your quote request form, we store the name, phone, email, address and
              message they provide, so that it appears in your leads list. That information belongs to you.
              We do not use it for any purpose other than showing it to you and your team inside the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">5. What we store and for how long</h2>
            <p>
              Connection credentials are stored encrypted and are kept only while the connection is active.
              Reports retrieved from Google are cached so the dashboard loads quickly. Everything is deleted
              when you disconnect the account or when your use of the Service ends.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">6. Who we share it with</h2>
            <p>
              Nobody. We do not sell, rent or trade any of this data. We do not use it to train machine
              learning models. The only third parties involved are the infrastructure providers that run the
              Service and the AI providers that generate draft text and images from the topics you enter;
              those providers never receive your Google reports or your leads.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">7. How to revoke access</h2>
            <p>
              You can disconnect any account at any time from the Connections screen in the Service. You can
              also revoke our access directly from your Google Account at{" "}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" className="text-[#0055FF]">
                myaccount.google.com/permissions
              </a>
              , or from Facebook under Settings → Business Integrations. Revoking access stops all data
              retrieval immediately and deletes the stored credential.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">8. Security</h2>
            <p>
              Access to the Service requires a Google sign-in. Credentials are stored encrypted. Each business
              account can only see its own data.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">9. Children</h2>
            <p>The Service is not directed to children and we do not knowingly collect data from anyone under 18.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">10. Changes</h2>
            <p>If this policy changes we will update the date at the top of this page.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-[#09090B]">11. Contact</h2>
            <p>
              Olimpo Web Design LLC — contacto@olimpo.tech —{" "}
              <a href="https://olimpo.tech" target="_blank" rel="noreferrer" className="text-[#0055FF]">
                https://olimpo.tech
              </a>
            </p>
          </section>
        </div>

        <footer className="mt-8 pt-6 border-t border-[#E4E4E7] text-sm text-[#71717A]" data-testid="privacy-footer">
          <a href="/" className="hover:text-[#0055FF]" data-testid="privacy-footer-home-link">Home</a>
          {" · "}
          <a href="/privacy" className="hover:text-[#0055FF]" data-testid="privacy-footer-privacy-link">Privacy Policy</a>
          {" · "}
          <a href="/terms" className="hover:text-[#0055FF]" data-testid="privacy-footer-terms-link">Terms of Service</a>
          {" · "}contacto@olimpo.tech
        </footer>
      </div>
    </div>
  );
}
